import { Buffer } from "buffer";
import {
  PublicKey,
  SystemProgram,
  TransactionInstruction,
  type AccountInfo,
} from "@solana/web3.js";

export const TOKEN_2022_PROGRAM_ID = new PublicKey(
  "TokenzQdBNbLqP5VEhdkAS6EPFLC1PHnBqCXEpPxuEb",
);
export const ASSOCIATED_TOKEN_PROGRAM_ID = new PublicKey(
  "ATokenGPvbdGVxr1b2hvZbsiqW5xWH25efTNsLJA8knL",
);

const MINT_BASE_SIZE = 82;
const TOKEN_ACCOUNT_SIZE = 165;
const MINT_ACCOUNT_TYPE_OFFSET = TOKEN_ACCOUNT_SIZE;
const TLV_START = MINT_ACCOUNT_TYPE_OFFSET + 1;
const TOKEN_METADATA_EXTENSION = 19;

function readU16(data: Uint8Array, offset: number): number {
  return data[offset] | (data[offset + 1] << 8);
}

function readU32(data: Uint8Array, offset: number): number {
  return (
    data[offset] +
    data[offset + 1] * 2 ** 8 +
    data[offset + 2] * 2 ** 16 +
    data[offset + 3] * 2 ** 24
  );
}

export function readU64(data: Uint8Array, offset: number): bigint {
  let value = 0n;
  for (let index = 7; index >= 0; index -= 1) {
    value = (value << 8n) | BigInt(data[offset + index]);
  }
  return value;
}

export function getToken2022Ata(mint: PublicKey, owner: PublicKey): PublicKey {
  if (!PublicKey.isOnCurve(owner.toBytes())) {
    throw new Error("The selected wallet is not a valid ed25519 key");
  }
  return PublicKey.findProgramAddressSync(
    [owner.toBytes(), TOKEN_2022_PROGRAM_ID.toBytes(), mint.toBytes()],
    ASSOCIATED_TOKEN_PROGRAM_ID,
  )[0];
}

export function createToken2022AtaIdempotentInstruction(params: {
  payer: PublicKey;
  ata: PublicKey;
  owner: PublicKey;
  mint: PublicKey;
}): TransactionInstruction {
  const { payer, ata, owner, mint } = params;
  return new TransactionInstruction({
    programId: ASSOCIATED_TOKEN_PROGRAM_ID,
    keys: [
      { pubkey: payer, isSigner: true, isWritable: true },
      { pubkey: ata, isSigner: false, isWritable: true },
      { pubkey: owner, isSigner: false, isWritable: false },
      { pubkey: mint, isSigner: false, isWritable: false },
      { pubkey: SystemProgram.programId, isSigner: false, isWritable: false },
      { pubkey: TOKEN_2022_PROGRAM_ID, isSigner: false, isWritable: false },
    ],
    data: Buffer.from([1]),
  });
}

export function readMintBase(account: AccountInfo<Buffer>): {
  supplyRaw: bigint;
  decimals: number;
} {
  if (!account.owner.equals(TOKEN_2022_PROGRAM_ID) || account.data.length < MINT_BASE_SIZE) {
    throw new Error("Invalid Token-2022 mint");
  }
  return {
    supplyRaw: readU64(account.data, 36),
    decimals: account.data[44],
  };
}

export interface EmbeddedTokenMetadata {
  name: string;
  symbol: string;
  uri: string;
}

function readString(data: Uint8Array, cursor: { offset: number }): string {
  if (cursor.offset + 4 > data.length) throw new Error("Truncated metadata");
  const length = readU32(data, cursor.offset);
  cursor.offset += 4;
  if (length < 0 || cursor.offset + length > data.length) {
    throw new Error("Invalid metadata length");
  }
  const value = new TextDecoder("utf-8", { fatal: true }).decode(
    data.subarray(cursor.offset, cursor.offset + length),
  );
  cursor.offset += length;
  return value;
}

export function readEmbeddedTokenMetadata(
  account: AccountInfo<Buffer>,
): EmbeddedTokenMetadata | undefined {
  if (!account.owner.equals(TOKEN_2022_PROGRAM_ID) || account.data.length <= TLV_START) {
    return undefined;
  }
  let offset = TLV_START;
  while (offset + 4 <= account.data.length) {
    const extensionType = readU16(account.data, offset);
    const extensionLength = readU16(account.data, offset + 2);
    const start = offset + 4;
    const end = start + extensionLength;
    if (end > account.data.length) break;
    if (extensionType === TOKEN_METADATA_EXTENSION) {
      const payload = account.data.subarray(start, end);
      if (payload.length < 64) return undefined;
      const cursor = { offset: 64 };
      return {
        name: readString(payload, cursor),
        symbol: readString(payload, cursor),
        uri: readString(payload, cursor),
      };
    }
    offset = end;
  }
  return undefined;
}
